import { useSuspenseQuery } from '@tanstack/react-query';
import type { FlowDetails } from '../types';
import queryKeys from './queryKeys';
import type { QueryConfig } from 'lib/react-query';
import { apiClient } from 'lib/api';
import { isAxiosError } from 'axios';

interface GetFlowDetailsParams {
  id?: string;
}

export const getFlowDetails = async (
  { id, ...params }: GetFlowDetailsParams,
  signal?: AbortSignal
): Promise<FlowDetails> => {
  const res = await apiClient.get<{ data: FlowDetails }>(
    `${queryKeys.flows}/${id}`,
    {
      signal,
      params,
    }
  );

  if (isAxiosError(res)) {
    const error = new Error(
      `Get flow details. Status: ${res.response?.status}`
    );
    error.cause = {
      ...res.response?.data,
      status: res.response?.status,
    };
    return Promise.reject(error);
  }

  return res.data.data;
};

interface UseFlowsOptions<TData> {
  config?: QueryConfig<typeof getFlowDetails, TData>;
  params: GetFlowDetailsParams;
}

export const useFlowDetails = <TData = FlowDetails>({
  config,
  params,
}: UseFlowsOptions<TData>) => {
  return useSuspenseQuery({
    staleTime: 60 * 30 * 1000,
    ...config,
    queryFn: ({ signal }) => getFlowDetails(params, signal),
    queryKey: [queryKeys.flows, params.id],
  });
};
